import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';

type Variant = 'primary' | 'blush' | 'quiet';

/*
  mint-400 is too light to carry white text (~2.1:1). The filled variants use mint-600 / blush-600,
  which clear AA against white, and lighten on hover rather than darken.
*/
const VARIANTS: Record<Variant, string> = {
  primary: 'bg-mint-600 text-white hover:bg-mint-500 shadow-soft',
  blush: 'bg-blush-600 text-white hover:bg-blush-500 shadow-petal',
  quiet: 'bg-white/80 text-mint-800 ring-1 ring-mint-200 hover:bg-white hover:ring-mint-300',
};

const BASE =
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full px-7 py-3.5 ' +
  'font-display text-base font-semibold transition-all duration-300 ease-out ' +
  'hover:-translate-y-0.5 active:translate-y-0 motion-reduce:hover:translate-y-0';

type CommonProps = {
  children: ReactNode;
  variant?: Variant;
  className?: string;
};

export function ButtonLink({
  to,
  children,
  variant = 'primary',
  className = '',
}: CommonProps & { to: string }) {
  return (
    <Link to={to} className={`${BASE} ${VARIANTS[variant]} ${className}`}>
      {children}
    </Link>
  );
}

export function Button({
  children,
  variant = 'primary',
  className = '',
  ...props
}: CommonProps & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      className={`${BASE} ${VARIANTS[variant]} disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 ${className}`}
    >
      {children}
    </button>
  );
}
